import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, ArrowRight, ArrowDown, ChevronDown, Check, Circle } from "lucide-react";
import usePageMeta from "@/hooks/usePageMeta";
import { ONDERZOEKEN, BRONNEN, KETEN } from "@/data/onderbouwing";
import { buildPilotMailto } from "@/lib/mailto";

const ease = [0.22, 1, 0.36, 1];
const fadeUp = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-8%" },
  transition: { duration: 0.8, ease },
};

const ROADMAP = [
  { jaar: "2026", titel: "Methodiek uitgeschreven", status: "done", d: "LevelLeven START is uitgewerkt als twaalfweeks programma met zeven kernbegrippen en zes pijlers." },
  { jaar: "2026", titel: "Bronnen in kaart", status: "done", d: "Relevante landelijke en lokale onderzoeken zijn verzameld en op deze pagina openbaar gemaakt." },
  { jaar: "Nu", titel: "Gesprekken met gemeenten", status: "current", d: "We verkennen met gemeenten waar een pilot zinvol is en hoe die aansluit op het bestaande aanbod." },
  { jaar: "Daarna", titel: "Eerste pilots met meting", status: "planned", d: "Start-, tussen- en eindmeting, waar afgesproken een vervolgmeting. Bij voorkeur met een onafhankelijke onderzoekspartner." },
  { jaar: "Daarna", titel: "Evaluatie en bijstelling", status: "planned", d: "Openbaar maken wat werkt, wat niet en voor wie. De methodiek aanpassen waar nodig." },
];

function Bron({ id }) {
  const b = BRONNEN[id];
  return (
    <li className="text-sm text-ll-navy/80 leading-relaxed">
      {b.apa}{" "}
      <a href={b.url} target="_blank" rel="noreferrer" data-testid={`bron-link-${id}`} className="text-ll-coral underline underline-offset-2 break-all hover:text-ll-ink">
        {b.url}
      </a>
    </li>
  );
}

function OnderzoekBlok({ o }) {
  return (
    <motion.article {...fadeUp} data-testid={`onderzoek-${o.k}`} className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-14 py-14 sm:py-16 border-t border-ll-ink/10">
      <div className="lg:col-span-4">
        <p className="text-[10px] uppercase tracking-[0.35em] text-ll-coral">Onderzoek {o.n} · {o.org}</p>
        <h3 className="mt-4 font-heading text-3xl sm:text-4xl font-light leading-tight">{o.kop}</h3>
        <p className="mt-8 chapter-num text-5xl sm:text-6xl leading-none text-ll-ink">{o.cijfer}</p>
        <p className="mt-3 text-sm text-ll-navy/70 leading-relaxed">{o.cijferLabel}</p>
        {o.extra && <p className="mt-2 text-sm text-ll-navy/70">{o.extra}</p>}
      </div>
      <div className="lg:col-span-8 space-y-5">
        {[
          { l: "Wat het onderzoek zegt", t: o.feit, c: "bg-white" },
          { l: "Wat wij daaruit halen", t: o.interpretatie, c: "bg-[#F0EAD8]" },
          { l: "Let op", t: o.nuance, c: "bg-ll-mist" },
        ].map((r) => (
          <div key={r.l} className={`rounded-2xl p-6 ${r.c} border border-ll-ink/5`}>
            <p className="text-[10px] uppercase tracking-[0.28em] text-ll-navy/60">{r.l}</p>
            <p className="mt-2 text-ll-ink leading-relaxed">{r.t}</p>
          </div>
        ))}
        <details className="group rounded-2xl border border-ll-ink/10 bg-white" data-testid={`onderzoek-bron-${o.k}`}>
          <summary className="cursor-pointer list-none flex items-center justify-between px-6 py-4 text-sm font-medium text-ll-ink">
            Broninformatie
            <ChevronDown size={16} className="transition-transform group-open:rotate-180" />
          </summary>
          <ul className="px-6 pb-5 space-y-3">{o.bronnen.map((id) => <Bron key={id} id={id} />)}</ul>
        </details>
      </div>
    </motion.article>
  );
}

function KetenFlow() {
  return (
    <ol className="grid grid-cols-1 lg:grid-cols-5 gap-3" data-testid="onderzoek-keten">
      {KETEN.map((k, i) => (
        <motion.li
          key={k}
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: i * 0.1, ease }}
          className={`relative rounded-2xl p-6 ${i === KETEN.length - 1 ? "bg-ll-coral text-white" : "bg-white/10 text-ll-cream"}`}
        >
          <span className="chapter-num text-sm opacity-70">0{i + 1}</span>
          <p className="mt-3 font-heading text-xl leading-snug">{k}</p>
          {i < KETEN.length - 1 && (
            <>
              <ArrowRight size={18} className="hidden lg:block absolute -right-3 top-1/2 -translate-y-1/2 text-ll-yellow z-10" />
              <ArrowDown size={18} className="lg:hidden absolute left-1/2 -bottom-3 -translate-x-1/2 text-ll-yellow z-10" />
            </>
          )}
        </motion.li>
      ))}
    </ol>
  );
}

export default function OnderzoekEnPilot() {
  usePageMeta({
    title: "Onderzoek & onderbouwing · LevelLeven",
    description:
      "Wat onafhankelijk onderzoek (CBS, SCP, Gemeente Amsterdam, Divosa, De Ruig) vertelt over participatie, langdurige bijstand en persoonlijke ontwikkeling. Met volledige bronvermelding.",
  });
  return (
    <div className="bg-ll-cream text-ll-ink">
      <section className="pt-16 pb-20 sm:pt-24 sm:pb-28">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16">
          <motion.div {...fadeUp} className="max-w-4xl">
            <p className="text-[10px] uppercase tracking-[0.35em] text-ll-coral">Onderzoek & onderbouwing</p>
            <h1 className="mt-6 font-heading font-light text-4xl sm:text-5xl lg:text-6xl leading-[1.03] tracking-tight" data-testid="onderzoek-title">
              Veel voorzieningen. <span className="italic text-ll-coral">Toch bereikt ondersteuning niet iedereen.</span>
            </h1>
            <p className="mt-8 text-lg text-ll-navy/80 leading-relaxed max-w-2xl">
              Wat onafhankelijk onderzoek ons vertelt over participatie, langdurige bijstand en persoonlijke ontwikkeling.
            </p>
          </motion.div>
          <motion.div {...fadeUp} className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-4 max-w-5xl">
            {[
              { l: "Feit", d: "Wat in een onderzoek staat. Met bron, jaartal en link." },
              { l: "Interpretatie", d: "Wat wij daaruit afleiden. Altijd als zodanig benoemd." },
              { l: "Ambitie", d: "Wat LevelLeven wil onderzoeken. Nog geen bewezen resultaat." },
            ].map((x) => (
              <div key={x.l} className="rounded-2xl border border-ll-ink/10 p-5 bg-white/70">
                <p className="font-heading text-xl">{x.l}</p>
                <p className="mt-1 text-sm text-ll-navy/70">{x.d}</p>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      <section className="pb-16">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16">
          {ONDERZOEKEN.map((o) => <OnderzoekBlok key={o.k} o={o} />)}
        </div>
      </section>

      <section className="py-24 sm:py-32 bg-ll-navy text-ll-cream">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16">
          <motion.div {...fadeUp} className="max-w-3xl mb-14">
            <p className="text-[10px] uppercase tracking-[0.35em] text-ll-yellow">De onderzoeken verbonden</p>
            <h2 className="mt-6 font-heading font-light text-4xl sm:text-5xl leading-[1.05]">
              Van veel aanbod <span className="italic text-ll-yellow">naar een passende route.</span>
            </h2>
          </motion.div>
          <KetenFlow />
          <p className="mt-12 max-w-3xl text-sm text-ll-cream/75 leading-relaxed" data-testid="onderzoek-nuance-landelijk">
            Belangrijk: deze landelijke en Amsterdamse gegevens laten patronen zien. Ze leveren geen rechtstreeks bewijs voor een tekort in iedere gemeente.
            Of en waar een aanvullende route nodig is, willen we per gemeente samen onderzoeken.
          </p>
        </div>
      </section>

      <section className="py-24 sm:py-28">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <p className="text-[10px] uppercase tracking-[0.35em] text-ll-coral">Waar staat LevelLeven nu?</p>
            <h2 className="mt-6 font-heading font-light text-4xl leading-tight">Eerlijk over <span className="italic text-ll-coral">wat er al is.</span></h2>
            <p className="mt-6 text-ll-navy/80 leading-relaxed">
              De effectiviteit van LevelLeven zelf is nog niet onderzocht. Resultaten van andere interventies zijn geen resultaten van LevelLeven.
            </p>
          </div>
          <ol className="lg:col-span-8 space-y-4" data-testid="pilot-roadmap">
            {ROADMAP.map((r, i) => (
              <li key={r.titel} data-testid={`roadmap-item-${i}`} className="flex gap-5 p-6 rounded-2xl bg-white border border-ll-ink/10">
                <span className={`mt-1 w-7 h-7 rounded-full shrink-0 flex items-center justify-center ${r.status === "done" ? "bg-ll-coral text-white" : r.status === "current" ? "bg-ll-navy text-ll-yellow" : "border border-ll-ink/20 text-ll-navy/40"}`}>
                  {r.status === "done" ? <Check size={14} /> : r.status === "current" ? <span className="w-2 h-2 rounded-full bg-ll-yellow animate-pulse" /> : <Circle size={10} />}
                </span>
                <div>
                  <p className="text-[10px] uppercase tracking-[0.28em] text-ll-navy/60">{r.jaar}</p>
                  <p className="mt-1 font-heading text-xl">{r.titel}</p>
                  <p className="mt-1 text-sm text-ll-navy/75">{r.d}</p>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </section>

      <section className="py-20 sm:py-24 bg-[#F0EAD8]">
        <div className="max-w-[1100px] mx-auto px-6 sm:px-10 lg:px-16">
          <p className="text-[10px] uppercase tracking-[0.35em] text-ll-coral">Bronnenlijst (APA 7)</p>
          <ul className="mt-8 space-y-4 pl-6 -indent-6" data-testid="bronnenlijst">
            {Object.keys(BRONNEN).map((id) => <Bron key={id} id={id} />)}
          </ul>
        </div>
      </section>

      <section className="py-20 sm:py-24">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16 flex flex-col md:flex-row md:items-end md:justify-between gap-8">
          <h2 className="font-heading font-light text-3xl sm:text-4xl leading-tight max-w-2xl">
            Laten we samen onderzoeken <span className="italic text-ll-coral">welke inwoners nog onvoldoende worden bereikt.</span>
          </h2>
          <div className="flex flex-col sm:flex-row gap-3">
            <a href={buildPilotMailto({ subject: "Verkennend gemeentegesprek LevelLeven" })} data-testid="onderzoek-cta-gesprek" className="inline-flex items-center gap-2 bg-ll-coral hover:bg-ll-navy text-white rounded-full px-6 py-3 transition-colors">
              Plan een verkennend gemeentegesprek <ArrowUpRight size={16} />
            </a>
            <Link to="/gemeenten" data-testid="onderzoek-cta-gemeenten" className="inline-flex items-center gap-2 border border-ll-ink/25 hover:bg-ll-ink hover:text-ll-cream rounded-full px-6 py-3 transition-colors">
              Aanpak voor gemeenten
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
